import { Injectable } from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { format } from "date-fns";
import { BaseApiService } from './api-base.service';
import { YData, AllowedDestinations } from "../models/ydata.model";
import { AllocationEventLogBatch } from "../models/allocation-event-log.model";
import { EcaseEvent, parseEcaseEvent } from 'src/app/models/ecaseevent.model';
import { HelperService } from './helper.service';
import { EventBusService } from './event-bus.service';

@Injectable({
  providedIn: 'root'
})
export class EventService extends BaseApiService {
    private env: string;

    constructor(
        http: HttpClient,
        msalService: MsalService,
        private helperService: HelperService,
        private eventBus: EventBusService
    ) {
        super(http, msalService);
        this.env = this.helperService.getStoredValue('ecase_env', 'prod');
    }

    // API call to get the timeline events
    public getTimelineEvents(start_date: string, includeMissing: boolean = false): Observable<{ ecaseevents: EcaseEvent[], ydata: YData, allowedDestinations: AllowedDestinations }> {
        const includeMissingParam = includeMissing ? '&include_missing=true' : '';
        return this.get<any>(`${this.apiUrl}/ecase/${this.env}/timeline/?start_date=${start_date}${includeMissingParam}`).pipe(
            map(response => {
                const ecaseevents = response && response.gridData
                    ? response.gridData.map((item: any) => parseEcaseEvent(item))
                    : [];

                const yunitData = response.yunitData || [];
                // Emit staff metadata through event bus
                this.eventBus.emitStaffMetadata(yunitData);
                
                const ydata: YData = {
                    yunitData: yunitData
                };
                const allowedDestinations: AllowedDestinations = response.allowedDestinations || {};
                return { ecaseevents, ydata, allowedDestinations };
            })
        );
    }

    // API call to allocate the service to the carer
    public allocateServiceInstance(ActivityInstanceId: string, rsAuthenId: string): Observable<any> {
        return this.get<any>(`${this.apiUrl}/ecase/${this.env}/allocate/?careActivityInstanceId=${ActivityInstanceId}&rsAuthenId=${rsAuthenId}`);
    }

    public unallocateServiceInstance(ActivityInstanceId: string): Observable<any> {
        return this.get<any>(`${this.apiUrl}/ecase/${this.env}/unallocate/?careActivityInstanceId=${ActivityInstanceId}`);
    }

    // Best-effort action log for future ML inference. Additive — does not
    // affect the allocate/unallocate calls. Callers fire-and-forget.
    public logAllocationBatch(batch: AllocationEventLogBatch): Observable<any> {
        return this.post<any>(`${this.apiUrl}/ecase/${this.env}/allocation-log/`, batch);
    }

    public updateActivityTiming(activityInstanceId: string, carerString: string, start: string, end: string): Observable<any> {
        const start_date = encodeURIComponent(format(new Date(start), 'yyyy-MM-dd HH:mm:ss'));
        const end_date = encodeURIComponent(format(new Date(end), 'yyyy-MM-dd HH:mm:ss'));
        return this.get<any>(`${this.apiUrl}/ecase/${this.env}/update-timing/?activityInstanceId=${activityInstanceId}&carerString=${carerString}&start=${start_date}&end=${end_date}`);
    }
}
